/**
 * Initialization hook for Generate Assessment Plan page
 *
 * Validates URL params, validates integration key via gateway API,
 * and gets/creates a scribe job. Mirrors use-public-recording-init.ts pattern.
 */

import { validateRequiredParams } from '@/lib/generate-assessment-plan-utils';
import {
  getOrCreateScribeJob,
  validateIntegrationKey,
} from '@/lib/public-recording-utils';
import { AuthStateSelector } from '@/store/selectors';
import type { GenerateAssessmentPlanParams } from '@/types/generate-assessment-plan';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

export const useGenerateAssessmentPlanInit = (
  params: GenerateAssessmentPlanParams,
) => {
  const [isValidated, setIsValidated] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [scribeJobId, setScribeJobId] = useState<string | null>(null);
  const [gatewayToken, setGatewayToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const hasInitialized = useRef(false);

  // Same partnerId source as the public scribe page
  const partnerIdFromStore = useSelector(AuthStateSelector.partnerId);

  const {
    appointmentId,
    patientId,
    locationId,
    integrationKey,
    partnerName,
    debugMode,
    patientFirstName,
    patientLastName,
    patientDob,
    patientCode,
  } = params;

  // Debug logging
  useEffect(() => {
    if (debugMode) {
      console.log('[GenerateAssessmentPlan] Debug mode enabled');
      console.log('[GenerateAssessmentPlan] Query Parameters:', {
        appointmentId: appointmentId || '(missing)',
        patientId: patientId || '(missing)',
        locationId: locationId || '(missing)',
        partnerIdFromStore: partnerIdFromStore || '(missing)',
        partnerName: partnerName || '(missing)',
        integrationKey: integrationKey
          ? '***' + integrationKey.slice(-4)
          : '(missing)',
        patientCode: patientCode || '(missing)',
        patientFirstName: patientFirstName || '(missing)',
        patientLastName: patientLastName || '(missing)',
        patientDob: patientDob || '(missing)',
      });
    }
  }, [
    debugMode,
    appointmentId,
    patientId,
    locationId,
    partnerIdFromStore,
    partnerName,
    integrationKey,
    patientCode,
    patientFirstName,
    patientLastName,
    patientDob,
  ]);

  // Validate and initialize
  useEffect(() => {
    if (hasInitialized.current) {
      console.log('[GenerateAssessmentPlan] Already initialized, skipping...');
      return;
    }

    const initialize = async () => {
      console.log('[GenerateAssessmentPlan] Initializing...');
      hasInitialized.current = true;
      setIsValidating(true);
      setError(null);

      // Validate required parameters
      const { valid, missingParams } = validateRequiredParams(params);
      if (!valid) {
        const errorMsg = `Missing required parameter${
          missingParams.length > 1 ? 's' : ''
        }: ${missingParams.join(', ')}`;
        console.error('[GenerateAssessmentPlan]', errorMsg);
        console.error(
          '[GenerateAssessmentPlan] Expected URL format: /generate-assessment-plan?appointmentId=XXX&patientId=XXX&locationId=XXX&partnerName=XXX&integrationKey=XXX&patientCode=XXX',
        );
        setError(errorMsg);
        setIsValidating(false);
        return;
      }

      console.log('[GenerateAssessmentPlan] All required parameters present');

      // Validate integration key (reuse existing utility)
      const validationResult = await validateIntegrationKey(
        partnerIdFromStore,
        integrationKey,
        partnerName,
      );
      if (!validationResult.valid) {
        console.error(
          '[GenerateAssessmentPlan] Integration validation failed:',
          validationResult.error,
        );
        setError(
          validationResult.error || 'Invalid partner ID or integration key',
        );
        setIsValidating(false);
        return;
      }

      if (!validationResult.token) {
        console.error(
          '[GenerateAssessmentPlan] Validation succeeded but no gateway token received',
        );
        setError('Authentication token not received from gateway');
        setIsValidating(false);
        return;
      }

      console.log(
        '[GenerateAssessmentPlan] Integration validated, gateway token received',
      );
      setGatewayToken(validationResult.token);
      setIsValidated(true);

      // Get or create scribe job (reuse existing utility)
      const result = await getOrCreateScribeJob(
        appointmentId,
        patientId,
        locationId,
        'assessmentAndPlan',
        patientFirstName,
        patientLastName,
        patientDob,
      );

      if (!result.jobId) {
        console.error(
          '[GenerateAssessmentPlan] Failed to setup scribe job:',
          result.error,
        );
        setError(result.error || 'Failed to setup session');
        setIsValidating(false);
        return;
      }

      console.log(
        '[GenerateAssessmentPlan] ✅ Initialization complete. Scribe Job ID:',
        result.jobId,
      );
      setScribeJobId(result.jobId);
      setIsValidating(false);
    };

    initialize();
  }, [
    appointmentId,
    patientId,
    locationId,
    integrationKey,
    partnerName,
    partnerIdFromStore,
    patientFirstName,
    patientLastName,
    patientDob,
    patientCode,
    params,
  ]);

  return {
    isValidated,
    isValidating,
    scribeJobId,
    gatewayToken,
    partnerId: partnerIdFromStore,
    error,
  };
};
